import React, { useState } from 'react';

const GetIncidents = () => {

  const API_KEY = 'DDgIwPc9wzmnxK0DmlVqkKQUjBOih1RdGKhY9800';

  // Set default: today at 00:00 and today at 23:59 (local time)
  function getDefaultDateTimes() {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    // datetime-local expects 'YYYY-MM-DDTHH:MM'
    const start = `${yyyy}-${mm}-${dd}T00:00`;
    const end = `${yyyy}-${mm}-${dd}T23:59`;
    return { start, end };
  }

  const defaults = getDefaultDateTimes();
  const [datetimeStart, setDatetimeStart] = useState(defaults.start);
  const [datetimeEnd, setDatetimeEnd] = useState(defaults.end);
  const [cityname, setCityname] = useState('');
  const [loading, setLoading] = useState(false);

   // Convert datetime-local values (YYYY-MM-DDTHH:mm) to %Y-%m-%dT%H:%M:%S.000Z
  function toAPIDatetime(dt) {
    if (!dt) return '';
    // Assumes input like '2024-06-10T15:30'
    const [date, time] = dt.split('T');
    if (!date || !time) return '';
    return `${date}T${time}:00.000Z`;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!datetimeStart || !datetimeEnd || !cityname) {
      alert('Please fill out all fields.');
      setLoading(false);
      return;
    }
  
    const datetimeStartFormatted = toAPIDatetime(datetimeStart);
    const datetimeEndFormatted = toAPIDatetime(datetimeEnd);
  
    const baseUrl = `https://api.crimeometer.com/v2/crime-incidents-by-city?datetime_ini=${encodeURIComponent(
      datetimeStartFormatted
    )}&datetime_end=${encodeURIComponent(datetimeEndFormatted)}&cityname=${encodeURIComponent(cityname)}`;
  
    try {
      // Fetch first page
      const firstResponse = await fetch(`${baseUrl}&page=1`, {
        method: 'GET',
        headers: {
          'x-api-key': API_KEY,
        },
      });
      const firstData = await firstResponse.json();
  
      if (!firstData.pages_count || firstData.pages_count === 1) {
        if (firstData.incidents && firstData.incidents.length > 0) {
          exportToCSV(firstData.incidents);
        } else {
          alert('No incidents found.');
        }
        setLoading(false);
        return;
      }
  
      // Fetch remaining pages in parallel
      const pageRequests = [];
      for (let i = 2; i <= firstData.pages_count; i++) {
        pageRequests.push(
          fetch(`${baseUrl}&page=${i}`, {
            method: 'GET',
            headers: {
              'x-api-key': API_KEY,
            },
          }).then((res) => res.json())
        );
      }
  
      const remainingData = await Promise.all(pageRequests);
  
      // Combine incidents from all pages
      const allIncidents = [
        ...firstData.incidents,
        ...remainingData.flatMap((page) => page.incidents || []),
      ];
  
      if (allIncidents.length > 0) {
        exportToCSV(allIncidents);
      } else {
        alert('No incidents found.');
      }
  
    } catch (err) {
      console.error('Error fetching incidents:', err);
    }
    setLoading(false);
  };
  
  // Helper to export CSV
  const exportToCSV = (incidents) => {
    // Pick only required fields
    const filtered = incidents.map((item) => ({
      incident_date: item.incident_date || '',
      incident_offense: item.incident_offense || '',
      incident_latitude: item.incident_latitude || '',
      incident_longitude: item.incident_longitude || '',
      incident_address: item.incident_address || '',
    }));
  
    const headers = [
      'incident_date',
      'incident_offense',
      'incident_latitude',
      'incident_longitude',
      'incident_address',
    ];
  
    // Convert to CSV
    const csvRows = [];
    csvRows.push(headers.join(',')); // header row
    filtered.forEach((obj) => {
      const row = headers.map((h) => `"${(obj[h] ?? '').toString().replace(/"/g, '""')}"`);
      csvRows.push(row.join(','));
    });
  
    const csvData = csvRows.join('\n');
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
  
    const a = document.createElement('a');
    a.href = url;
    a.download = 'crime_incidents.csv';
    a.click();
    URL.revokeObjectURL(url);
  
    console.log('CSV exported successfully!');
  };
  
  // Simple spinner style
  const spinner = (
    <div style={{
      display: 'inline-block',
      width: 24,
      height: 24,
      border: '3px solid #ccc',
      borderRadius: '50%',
      borderTop: '3px solid #333',
      animation: 'spin 1s linear infinite',
      marginLeft: 10,
      verticalAlign: 'middle'
    }} />
  );

  // Add a keyframes style tag for spinner animation (since inline styles can't do keyframes)
  // This is one way to achieve it without an external CSS file.
  React.useEffect(() => {
    // Only add once
    if (!document.getElementById('getincidents-spinner-style')) {
      const style = document.createElement('style');
      style.id = 'getincidents-spinner-style';
      style.innerHTML = `
      @keyframes spin {
        0% { transform: rotate(0deg);}
        100% { transform: rotate(360deg);}
      }
      `;
      document.head.appendChild(style);
    }
  }, []);

  return (
    <div style={{ padding: 24, maxWidth: 500, margin: '0 auto', fontFamily: 'Segoe UI, Arial, sans-serif' }}>
      <h2>Crime Incidents Fetcher</h2>
      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: 16 }}>
          <label>
            Start Datetime:
            <input
              type="datetime-local"
              value={datetimeStart}
              onChange={e => setDatetimeStart(e.target.value)}
              style={{ marginLeft: 8, padding: '4px 8px', width: '60%' }}
              required
              disabled={loading}
            />
          </label>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label>
            End Datetime:
            <input
              type="datetime-local"
              value={datetimeEnd}
              onChange={e => setDatetimeEnd(e.target.value)}
              style={{ marginLeft: 8, padding: '4px 8px', width: '60%' }}
              required
              disabled={loading}
            />
          </label>
        </div>
        <div style={{ marginBottom: 16 }}>
          <label>
            City Name:
            <input
              type="text"
              value={cityname}
              onChange={e => setCityname(e.target.value)}
              style={{ marginLeft: 8, padding: '4px 8px', width: '60%' }}
              required
              disabled={loading}
            />
          </label>
        </div>
        <button
          type="submit"
          disabled={loading}
          style={{ padding: '8px 18px', fontWeight: 'bold', minWidth: 120 }}
        >
          {loading ? (
            <>
              Processing...
              {spinner}
            </>
          ) : (
            "Get incidents"
          )}
        </button>
      </form>
    </div>
  );
}

export default GetIncidents;